// apps/frontend/src/pages/pipelines/components/FloatingPreviewPanel.tsx
import { useEffect, useState } from 'react';
import { X, RefreshCw, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PreviewData } from '../types/pipeline';

interface FloatingPreviewPanelProps {
  nodeId: string | null;
  preview: PreviewData | null;
  isLoading: boolean;
  error: Error | null;
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => void;
}

const DEFAULT_POSITION = { x: 100, y: 100 };
const DEFAULT_SIZE = { width: 600, height: 400 };

export function FloatingPreviewPanel({
  nodeId: _nodeId,
  preview,
  isLoading,
  error,
  isOpen,
  onClose,
  onRefresh,
}: FloatingPreviewPanelProps) {
  void _nodeId;
  const [position, setPosition] = useState(DEFAULT_POSITION);
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Load position and size from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('preview-panel-state');
    if (saved) {
      try {
        const state = JSON.parse(saved);
        setPosition(state.position || DEFAULT_POSITION);
        setSize(state.size || DEFAULT_SIZE);
      } catch (e) {
        // Ignore invalid saved state
      }
    }
  }, []);

  // Save position and size to localStorage
  useEffect(() => {
    const state = { position, size };
    localStorage.setItem('preview-panel-state', JSON.stringify(state));
  }, [position, size]);

  if (!isOpen) return null;

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const panelStyle = isFullscreen
    ? {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        zIndex: 50,
      }
    : {
        position: 'fixed' as const,
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex: 50,
      };

  return (
    <Card
      className="shadow-2xl border-2 flex flex-col overflow-hidden"
      style={panelStyle}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/50">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-sm">Data Preview</h3>
          {preview?.metadata?.message && (
            <Badge variant="secondary" className="text-xs">
              {preview.metadata.message}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={isLoading}
            className="h-7 w-7 p-0"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleFullscreen}
            className="h-7 w-7 p-0"
          >
            {isFullscreen ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-7 w-7 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {isLoading && (
          <div className="flex items-center justify-center h-full">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading preview...</span>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <p className="text-destructive font-medium">Failed to load preview</p>
            <p className="text-sm text-muted-foreground">{error.message}</p>
            <Button onClick={onRefresh} variant="outline" size="sm">
              Retry
            </Button>
          </div>
        )}

        {!isLoading && !error && !preview && (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">Select a node to preview data</p>
          </div>
        )}

        {!isLoading && !error && preview && (
          <div className="space-y-4">
            {/* Debug Info */}
            {preview.metadata && (
              <div className="rounded-md bg-muted/50 p-3 text-xs">
                <div className="font-semibold mb-1">Debug Info:</div>
                <div className="space-y-1 text-muted-foreground">
                  {preview.metadata.sourceNode && (
                    <div>Data Source ID: <span className="font-mono">{preview.metadata.sourceNode}</span></div>
                  )}
                  {preview.metadata.nodeType && (
                    <div>Node Type: {preview.metadata.nodeType}</div>
                  )}
                  {preview.metadata.message && (
                    <div className="text-orange-600 font-medium">⚠️ {preview.metadata.message}</div>
                  )}
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="flex items-center gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Rows:</span>{' '}
                <span className="font-medium">
                  {preview.stats.sampledRows}
                  {preview.stats.totalRows && ` of ${preview.stats.totalRows}`}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Execution:</span>{' '}
                <span className="font-medium">{preview.stats.executionTime}ms</span>
              </div>
              <div>
                <span className="text-muted-foreground">Columns:</span>{' '}
                <span className="font-medium">{preview.schema.columns.length}</span>
              </div>
            </div>

            {/* Table */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    {preview.schema.columns.map((col) => (
                      <TableHead key={col.name}>
                        <div className="flex flex-col">
                          <span>{col.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {col.type}
                            {col.nullable && ' | null'}
                          </span>
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.data.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={preview.schema.columns.length}
                        className="text-center text-muted-foreground"
                      >
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    preview.data.map((row: any, idx) => (
                      <TableRow key={idx}>
                        {preview.schema.columns.map((col) => (
                          <TableCell key={col.name}>
                            {formatCellValue(row[col.name])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// Helper to format cell values
function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}
